// The lazy loaders for every mode's data files, and nothing else.
//
// A mode's files are declared in `src/modes.js` (`data`), by name under
// `src/data/`. They are NEVER in the first load: the entry chunk may reach none
// of them (tests/entry-chunk.test.js reads the registry and fails if it does),
// and the only way in is a dynamic `import()` in the table below. A literal
// path per file, rather than a computed one, because the bundler can only split
// what it can see: a template string would pull every JSON file in the
// directory into the build as a chunk.
//
// ADDING A MODE'S DATA is one line here. `tests/modes.test.js` checks that every
// file a declaration names has a loader, so a declaration cannot ship without.

const LOADERS = {
  'tram-routes.melbourne.json': () => import('./data/tram-routes.melbourne.json'),
  // The tram stops carry the Free Tram Zone corrections (src/tram-ftz.js).
  'trams.melbourne.json': async () => {
    const [bundle, fixes, { applyFtzOverrides }] = await Promise.all([
      import('./data/trams.melbourne.json'),
      import('./data/tram-ftz-overrides.json'),
      import('./tram-ftz.js'),
    ]);
    return applyFtzOverrides(bundle.default ?? bundle, fixes.default ?? fixes);
  },
  // Melbourne's buses. Declared and loadable, hidden until the UI phase: nothing
  // asks for these while the mode is `hidden`, so no phone fetches either chunk.
  'bus-routes.melbourne.json': () => import('./data/bus-routes.melbourne.json'),
  'buses.melbourne.json': () => import('./data/buses.melbourne.json'),
};

/** The file names this table can load. */
export const loadableFiles = () => Object.keys(LOADERS);

/** The key a mode's state and caches are held under: the city and the id. */
export const modeKey = (mode) => `${mode.city}/${mode.id}`;

/**
 * One of a mode's data files ('routes' or 'stops'), parsed. Resolves to null
 * when the mode declares none or the chunk cannot be read, never rejects: every
 * reader treats null as "nothing to show", which is the truth for a phone that
 * could not fetch it.
 */
export async function loadModeData(mode, which) {
  const file = mode?.data?.[which];
  const load = file ? LOADERS[file] : null;
  if (!load) return null;
  try {
    const m = await load();
    return m.default ?? m;
  } catch {
    return null;
  }
}

/** Tests only: put a loader in place for a fixture mode's file, or take it away. */
export function useLoader(file, loader) {
  if (loader) LOADERS[file] = loader; else delete LOADERS[file];
}
